import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from '../common/ThemeToggle';
import './MobileMenu.css';

function MobileMenu({ isOpen, navigationConfig, permissions, currentPath, onClose, onLogout, showEnvSwitch, envSwitchUrl, envSwitchLabel }) {
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedNested, setExpandedNested] = useState({});

  // Handle body scroll lock when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isOpen]);

  // Handle click on overlay (outside menu content)
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('mobile-menu')) {
      onClose();
    }
  };

  const toggleExpanded = function(itemId) {
    setExpandedItems(function(prev) {
      const newState = {};
      Object.keys(prev).forEach(function(key) {
        newState[key] = prev[key];
      });
      newState[itemId] = !prev[itemId];
      return newState;
    });
  };

  const toggleNested = function(parentId, index) {
    const key = parentId + '-' + index;
    setExpandedNested(function(prev) {
      const newState = {};
      Object.keys(prev).forEach(function(k) {
        newState[k] = prev[k];
      });
      newState[key] = !prev[key];
      return newState;
    });
  };

  const shouldShowItem = function(item) {
    if (!item.requiresPermission) return true;
    return permissions[item.requiresPermission];
  };

  const handleLinkClick = function() {
    onClose();
    setExpandedItems({});
    setExpandedNested({});
  };

  const handleLogout = function() {
    onClose();
    onLogout();
  };

  const menuClassName = isOpen ? 'mobile-menu open' : 'mobile-menu';

  return (
    <div className={menuClassName} onClick={handleOverlayClick}>
      <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
        <nav className="mobile-nav">
          {showEnvSwitch && (
            <a
              href={envSwitchUrl}
              className="mobile-nav-link"
              style={{ textDecoration: 'none' }}
            >
              {envSwitchLabel}
            </a>
          )}
          {navigationConfig.map(function(item) {
            if (!shouldShowItem(item)) return null;

            if (item.type === 'dropdown') {
              const isExpanded = expandedItems[item.id];
              const headerClassName = currentPath.startsWith('/' + item.id) 
                ? 'mobile-nav-link active' 
                : 'mobile-nav-link';
              const toggleClassName = isExpanded 
                ? 'expand-toggle expanded' 
                : 'expand-toggle';

              return (
                <div key={item.id} className="mobile-nav-section">
                  <div className="mobile-nav-header">
                    <Link
                      to={item.path}
                      className={headerClassName}
                      onClick={handleLinkClick}
                    >
                      {item.label}
                    </Link>
                    <button
                      className={toggleClassName}
                      onClick={function() { toggleExpanded(item.id); }}
                      aria-label={'Toggle ' + item.label + ' menu'}
                    >
                      <span className="expand-icon"></span>
                    </button>
                  </div>

                  {isExpanded ? (
                    <div className="mobile-submenu">
                      {item.items.map(function(subItem, index) {
                        if (!shouldShowItem(subItem)) return null;

                        if (subItem.type === 'separator') {
                          return (
                            <div key={index} className="mobile-separator">
                              <div className="mobile-separator-line" />
                              {subItem.label && (
                                <span className="mobile-separator-label">{subItem.label}</span>
                              )}
                            </div>
                          );
                        }

                        if (subItem.type === 'nested') {
                          const nestedKey = item.id + '-' + index;
                          const isNestedExpanded = expandedNested[nestedKey];
                          const nestedToggleClassName = isNestedExpanded 
                            ? 'expand-toggle expanded' 
                            : 'expand-toggle';

                          return (
                            <div key={index} className="mobile-nested-section">
                              <div className="mobile-nested-header">
                                <span className="mobile-nested-label">
                                  {subItem.label}
                                </span>
                                <button
                                  className={nestedToggleClassName}
                                  onClick={function() { toggleNested(item.id, index); }}
                                  aria-label={'Toggle ' + subItem.label + ' menu'}
                                >
                                  <span className="expand-icon"></span>
                                </button>
                              </div>

                              {isNestedExpanded ? (
                                <div className="mobile-nested-menu">
                                  {subItem.items.map(function(nestedItem, nestedIndex) {
                                    const nestedClassName = currentPath === nestedItem.path 
                                      ? 'mobile-nav-link nested active' 
                                      : 'mobile-nav-link nested';
                                    
                                    return (
                                      <Link
                                        key={nestedIndex}
                                        to={nestedItem.path}
                                        className={nestedClassName}
                                        onClick={handleLinkClick}
                                      >
                                        {nestedItem.label}
                                      </Link>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        const subItemClassName = currentPath === subItem.path 
                          ? 'mobile-nav-link active' 
                          : 'mobile-nav-link';

                        return (
                          <Link
                            key={index}
                            to={subItem.path}
                            className={subItemClassName}
                            onClick={handleLinkClick}
                          >
                            {subItem.label}
                            {subItem.badge > 0 && (
                              <span className="mobile-nav-badge">{subItem.badge}</span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            }

            if (item.type === 'external') {
              return (
                <a
                  key={item.id}
                  href={item.url}
                  target={item.target || '_self'}
                  rel="noopener noreferrer"
                  className="mobile-nav-link"
                  onClick={handleLinkClick}
                >
                  {item.label}
                  {item.target === '_blank' ? (
                    <span className="external-icon">↗</span>
                  ) : null}
                </a>
              );
            }

            const linkClassName = currentPath === item.path 
              ? 'mobile-nav-link active' 
              : 'mobile-nav-link';

            return (
              <Link
                key={item.id}
                to={item.path}
                className={linkClassName}
                onClick={handleLinkClick}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="mobile-menu-footer">
          <div className="mobile-theme-toggle">
            <ThemeToggle />
          </div>
          <button className="mobile-logout-button" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileMenu;